import type { PreviewBlock } from '$lib/model/types';

// Compose a 1-bit mask onto a canvas using the leaf's preview style.
// `mask` is RGBA where BLACK (R<128) means ink-on and WHITE means no ink.
// The function paints the leaf color wherever ink is on, blended into the
// target canvas with the chosen ink mode and opacity.

export function compositeLeafIntoCanvas(
  into: OffscreenCanvasRenderingContext2D,
  mask: ImageData,
  preview: PreviewBlock,
): void {
  const w = mask.width;
  const h = mask.height;
  // Step 1: convert mask to alpha-only bitmap (ink → opaque, no-ink → clear).
  // A pixel is "ink-on" only when it is part of the source content area
  // (mask alpha >= 128) AND its grey value is black-ish (R < 128).
  // Letterbox / out-of-content pixels (alpha = 0) are never ink, even though
  // their RGB happens to be (0,0,0).
  const colored = new ImageData(w, h);
  const src = mask.data;
  const dst = colored.data;
  const len = src.length;
  for (let i = 0; i < len; i += 4) {
    const a = src[i + 3] ?? 0;
    if (a < 128) continue;
    const ink = (src[i] ?? 255) < 128;
    if (ink) {
      dst[i + 3] = 255;
    }
  }

  const scratch = new OffscreenCanvas(w, h);
  const sctx = scratch.getContext('2d');
  if (!sctx) return;
  sctx.putImageData(colored, 0, 0);
  // Replace RGB with the leaf color, preserving the alpha pattern.
  sctx.globalCompositeOperation = 'source-in';
  sctx.fillStyle = preview.color;
  sctx.fillRect(0, 0, w, h);
  sctx.globalCompositeOperation = 'source-over';

  into.globalAlpha = Math.max(0, Math.min(100, preview.opacity)) / 100;
  into.globalCompositeOperation =
    preview.ink_mode === 'opaque' ? 'source-over' : 'multiply';
  into.drawImage(scratch, 0, 0);
  into.globalAlpha = 1;
  into.globalCompositeOperation = 'source-over';
}
